/**
 * MED-1400: selection rules for the Session Association picker, so a resource
 * can be linked to every session, or every session of one group, in one click.
 */

/** Adds `ids` to `value`, keeping the order of `value` and skipping duplicates. */
export function addSessionIds(value: string[], ids: string[]): string[] {
  const seen = new Set(value);
  const next = [...value];
  for (const id of ids) {
    if (!seen.has(id)) {
      seen.add(id);
      next.push(id);
    }
  }
  return next;
}

/** Removes `ids` from `value`. */
export function removeSessionIds(value: string[], ids: string[]): string[] {
  const drop = new Set(ids);
  return value.filter((id) => !drop.has(id));
}

/** True when `ids` is not empty and every id in it is selected. */
export function allIdsSelected(value: string[], ids: string[]): boolean {
  if (ids.length === 0) return false;
  const selected = new Set(value);
  return ids.every((id) => selected.has(id));
}

/** True when at least one id in `ids` is selected. */
export function someIdsSelected(value: string[], ids: string[]): boolean {
  const selected = new Set(value);
  return ids.some((id) => selected.has(id));
}

/**
 * Checks or clears a whole set of sessions. With nothing left selected the
 * resource goes back to General, as when the last single session is cleared.
 */
export function applySelectAll(
  value: string[],
  ids: string[],
  checked: boolean,
): { sessionIds: string[]; isGeneral: boolean } {
  const sessionIds = checked ? addSessionIds(value, ids) : removeSessionIds(value, ids);
  return { sessionIds, isGeneral: sessionIds.length === 0 };
}

/**
 * MED-1400: true for a session a resource can link to from the picker. 1:1
 * sessions are left out; the 1:1 drawer saves its own session with no picker.
 * Every picker filters with this, so "All sessions" is the same set everywhere.
 */
export function isLinkableSession(session: { session_type?: string | null }): boolean {
  return (
    session.session_type !== 'one_on_one_session' &&
    session.session_type !== 'non_coaching_one_on_one'
  );
}

/** MED-1400: a group named only "4" reads as a number, so show it as "Group 4". */
export function groupLabel(name: string): string {
  const trimmed = name.trim();
  return /^\d+$/.test(trimmed) ? `Group ${trimmed}` : name;
}

/** MED-1400: sorts group names so "2" comes before "10". */
export function compareGroupNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}
